import { useAppDispatch, useAppSelector } from '@/app/reduxHooks';
import { store } from '@/app/store';
import { syncCanvasState } from '@/features/adp/state/canvas/canvasSlice';
import ExplorerColumnsTable, {
  ExplorerColumnRow,
} from '@/features/adp/components/source/dialogs/explorer-dialog/components/ExplorerColumnsTable';
import { useAdpSessionStore } from '@/features/adp/hooks/useAdpSessionStore';
import { useSourceData } from '@/features/adp/hooks/useSourceData';
import { PersistedSourceConfigurationState } from '@/features/adp/hooks/useFinalizeSourceConfigurationMutation';
import { useCreateSourceFileAndFieldsMutation } from '@/features/adp/mutations/useSourceMutations';
import { useGetColumnsQuery } from '@/features/adp/queries/useConnectionQueries';
import { sourceService } from '@/features/adp/services/source/source.service';
import { BaseDatabaseConfig } from '@/features/adp/services/common/common.types';
import { buildCreateSourceFileAndFieldsPayload } from '@/features/adp/components/source/dialogs/explorer-dialog/utils/sourceFilePayloadBuilder';
import { closeOverlayAsync } from '@/features/adp/state/overlay.thunks';
import { ExplorerDialogOverlayData, openOverlay } from '@/features/adp/state/overlaySlice';
import { DataTypes } from '@/features/adp/types/data.types';
import { DataSourceType } from '@/features/adp/types/data-source.types';
import { classifyFileExtension } from '@/features/adp/utils/fileExtension.util';
import { DELIMITERS, DATE_FORMATS } from '@/features/adp/constants/datasource.constants';
import { Button } from '@/ui/button';
import {
  DialogFooter,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/ui/form';
import { Input } from '@/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/select';
import { Skeleton } from '@/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/ui/tooltip';
import { ArrowLeftIcon, InfoIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

type ExplorerFormValues = {
  connection: string;
  tableName: string;
  fileDelimiter: string;
  jsonPath: string;
  dateFormat: string;
};

const DELIMITER_NAME_TO_VALUE = new Map(DELIMITERS.map(d => [d.name, d.value]));

function normalizeDataType(value: string): string {
  const normalized = value.trim().toLowerCase();
  const supported = Object.values(DataTypes);
  return supported.includes(normalized as DataTypes) ? normalized : DataTypes.string;
}

const ExplorerDialog = () => {
  const { isOpen, data, type } = useAppSelector(state => state.overlay);
  const isVisible = isOpen && type === 'explorer-dialog';
  const explorerDialogData: ExplorerDialogOverlayData | null =
    isVisible && data != null ? (data as ExplorerDialogOverlayData) : null;
  const dispatch = useAppDispatch();
  const [rows, setRows] = useState<ExplorerColumnRow[]>([]);
  const { updateSourceData } = useSourceData();
  const { getNode } = useAdpSessionStore();
  const createSourceFileAndFieldsMutation = useCreateSourceFileAndFieldsMutation();
  const form = useForm<ExplorerFormValues>({
    defaultValues: {
      connection: '',
      tableName: '',
      fileDelimiter: '',
      jsonPath: '',
      dateFormat: '',
    },
  });

  const sourceEntry = useMemo(() => {
    const nodeId = explorerDialogData?.nodeId;
    if (!nodeId) return null;
    const node = getNode(nodeId) as {
      sourceConfigurationState?: PersistedSourceConfigurationState;
    } | null;
    return node?.sourceConfigurationState ?? null;
  }, [explorerDialogData?.nodeId, getNode]);

  const sourceName = (sourceEntry?.source_name ?? '').trim();

  const dataSourceType = (explorerDialogData?.data_source_type ?? '').trim();
  const normalizedSourceType = dataSourceType.toUpperCase();
  const isFileSystemSource = normalizedSourceType === DataSourceType.FILESYSTEM;

  const submittedPayload = sourceEntry?.submitted_source_payload as
    | Record<string, unknown>
    | undefined;
  const submittedInstance = typeof submittedPayload?.type === 'string' ? submittedPayload.type : '';

  const connectionLabel = dataSourceType
    ? `${dataSourceType.charAt(0).toUpperCase() + dataSourceType.slice(1).toLowerCase()} Connection`
    : 'Connection';

  const fallbackTablesFromConfiguration = useMemo(() => {
    const nodeId = explorerDialogData?.nodeId;
    if (!nodeId) return [];
    const node = getNode(nodeId) as { sourceConfigurationState?: { tables?: string[] } } | null;
    const tables = node?.sourceConfigurationState?.tables;
    if (!Array.isArray(tables)) return [];
    return tables.filter((item): item is string => typeof item === 'string');
  }, [explorerDialogData?.nodeId, getNode]);

  const sourceTables = sourceEntry?.tables ?? [];
  const selectableTables = sourceTables.length > 0 ? sourceTables : fallbackTablesFromConfiguration;

  /**
   * Table the source was configured with, persisted at
   * `sourceData.data.source_selected_table`. On revisit (gear icon → Explorer)
   * the form should reopen with this table pre-selected so the columns load
   * by default. Empty for a fresh source that hasn't picked a table yet.
   */
  const persistedSelectedTable = useMemo(() => {
    const nodeId = explorerDialogData?.nodeId;
    if (!nodeId) return '';
    const node = getNode(nodeId) as
      | { sourceData?: { data?: { source_selected_table?: unknown } } }
      | null;
    const table = node?.sourceData?.data?.source_selected_table;
    return typeof table === 'string' ? table : '';
  }, [explorerDialogData?.nodeId, getNode]);

  const selectedTable = form.watch('tableName');
  const selectedDelimiterName = form.watch('fileDelimiter');
  const selectedJsonPath = form.watch('jsonPath');
  const selectedDateFormat = form.watch('dateFormat');

  const fileKind = useMemo(
    () => (isFileSystemSource ? classifyFileExtension(selectedTable) : 'other'),
    [isFileSystemSource, selectedTable]
  );

  const showDelimiter = isFileSystemSource && fileKind === 'csv';
  const showJsonPath = isFileSystemSource && fileKind === 'json';
  const showDateFormat = isFileSystemSource && fileKind !== 'parquet' && Boolean(selectedTable);

  const resolvedDelimiterValue = useMemo(() => {
    if (!showDelimiter) return '';
    return DELIMITER_NAME_TO_VALUE.get(selectedDelimiterName) ?? selectedDelimiterName ?? '';
  }, [selectedDelimiterName, showDelimiter]);

  const fileExtras = useMemo(
    () => ({
      fileDelimiter: showDelimiter ? resolvedDelimiterValue : undefined,
      jsonPath: showJsonPath ? selectedJsonPath : undefined,
      dateFormat: showDateFormat ? selectedDateFormat : undefined,
    }),
    [
      resolvedDelimiterValue,
      selectedDateFormat,
      selectedJsonPath,
      showDateFormat,
      showDelimiter,
      showJsonPath,
    ]
  );

  const fileExtrasReady = useMemo(() => {
    if (!isFileSystemSource) return true;
    if (!selectedTable) return false;
    if (showDelimiter && !resolvedDelimiterValue) return false;
    if (showDateFormat && !selectedDateFormat) return false;
    return true;
  }, [
    isFileSystemSource,
    selectedTable,
    showDelimiter,
    resolvedDelimiterValue,
    showDateFormat,
    selectedDateFormat,
  ]);

  const selectedRows = useMemo(() => rows.filter(row => row.selected), [rows]);

  const columnsPayload = useMemo(() => {
    if (!selectedTable) return null;
    if (!sourceEntry?.submitted_source_payload) return null;
    if (!fileExtrasReady) return null;
    const payloadType = submittedInstance.trim().length > 0 ? submittedInstance : '';
    const databasePayload: BaseDatabaseConfig & Record<string, unknown> = {
      ...(sourceEntry.submitted_source_payload as Record<string, unknown>),
      type: payloadType,
      source_name: sourceName || null,
    };

    if (isFileSystemSource) {
      // /connections/columns rejects `path` (DTO whitelist). Angular only sends
      // table_name + file-extension extras here; `path`/`table_path` belongs to
      // the downstream /source-files payload (handled in sourceFilePayloadBuilder).
      databasePayload.table_name = selectedTable;
      databasePayload.file_name = selectedTable;
      if (showDelimiter) databasePayload.file_delimiter = resolvedDelimiterValue;
      if (showJsonPath) databasePayload.json_path = selectedJsonPath;
      if (showDateFormat) databasePayload.date_format = selectedDateFormat;
    } else {
      databasePayload.table_name = selectedTable;
    }

    return { databases: [databasePayload] };
  }, [
    fileExtrasReady,
    isFileSystemSource,
    resolvedDelimiterValue,
    selectedDateFormat,
    selectedJsonPath,
    selectedTable,
    showDateFormat,
    showDelimiter,
    showJsonPath,
    sourceEntry?.submitted_source_payload,
    sourceName,
    submittedInstance,
  ]);

  const columnsQuery = useGetColumnsQuery(columnsPayload ?? { databases: [] }, {
    enabled: Boolean(columnsPayload) && Boolean(selectedTable) && fileExtrasReady,
  });

  const dataTypeOptions = useMemo(() => Object.values(DataTypes), []);

  useEffect(() => {
    form.setValue('connection', sourceName);
  }, [form, sourceName]);

  useEffect(() => {
    form.reset({
      connection: sourceName,
      // Revisit: reopen with the previously-configured table selected so the
      // columns load by default. Fresh source → '' (no persisted table yet).
      tableName: persistedSelectedTable,
      fileDelimiter: '',
      jsonPath: '',
      dateFormat: '',
    });
    setRows([]);
  }, [form, explorerDialogData?.nodeId, sourceName, persistedSelectedTable]);

  useEffect(() => {
    setRows([]);
    form.setValue('fileDelimiter', '');
    form.setValue('jsonPath', '');
    form.setValue('dateFormat', '');
  }, [form, selectedTable]);

  useEffect(() => {
    if (!columnsQuery.isSuccess) return;

    // Revisit: read previously-saved source_files for this node. When present
    // AND the saved selection was for the currently-chosen table, restore
    // per-column `selected` and `primaryKey` so a prior de-selection isn't
    // silently re-checked. On first save OR when the user switched tables,
    // default to all-checked — otherwise switching tables on revisit would
    // leave every row unchecked (the saved names won't match a new schema).
    const nodeId = explorerDialogData?.nodeId;
    const sourceSession = nodeId
      ? (getNode(nodeId) as
          | { sourceData?: { data?: { source_files?: unknown; source_selected_table?: unknown } } }
          | null)?.sourceData?.data
      : null;
    const savedFiles = sourceSession?.source_files;
    const savedTable =
      typeof sourceSession?.source_selected_table === 'string'
        ? sourceSession.source_selected_table
        : null;
    const savedByName = new Map<string, { is_business_primary_key?: boolean }>();
    if (savedTable === selectedTable && Array.isArray(savedFiles)) {
      for (const f of savedFiles) {
        if (!f || typeof f !== 'object') continue;
        const o = f as Record<string, unknown>;
        if (typeof o.field_name === 'string') {
          savedByName.set(o.field_name, {
            is_business_primary_key: o.is_business_primary_key === true,
          });
        }
      }
    }
    const hasSavedSelection = savedByName.size > 0;

    const mappedRows = columnsQuery.data.map(column => {
      const saved = savedByName.get(column.column_name);
      return {
        id: column.column_name,
        // First save or after table-switch: default all-checked. Revisit on the
        // same table: only previously-saved columns.
        selected: hasSavedSelection ? Boolean(saved) : true,
        columnName: column.column_name,
        dataType: normalizeDataType(column.data_type),
        primaryKey: saved?.is_business_primary_key === true,
        dqChecks: [],
      };
    });
    setRows(mappedRows);
  }, [columnsQuery.data, columnsQuery.isSuccess, explorerDialogData?.nodeId, getNode, selectedTable]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      dispatch(closeOverlayAsync());
    }
  };

  const handleBack = () => {};

  const hasTableSelected = Boolean(selectedTable);
  const canSave =
    hasTableSelected &&
    fileExtrasReady &&
    columnsQuery.isSuccess &&
    selectedRows.length > 0 &&
    !createSourceFileAndFieldsMutation.isPending;

  const tableLabel = isFileSystemSource ? 'File Name' : 'Table Name';
  const tablePlaceholder = isFileSystemSource ? 'Select a file' : 'Select a table';
  const tableTooltip = isFileSystemSource
    ? 'Pick a file to load its columns.'
    : 'Pick a table to load its columns.';

  const updateRow = (rowId: string, updates: Partial<ExplorerColumnRow>) => {
    setRows(previousRows =>
      previousRows.map(row => (row.id === rowId ? { ...row, ...updates } : row))
    );
  };

  /** Read `source_data_source_id` from node session — needed for the pre-check. */
  const readSourceDataSourceId = (nodeId: string): number => {
    const node = getNode(nodeId) as {
      sourceData?: { data?: { source_data_source_id?: number } };
    } | null;
    const id = node?.sourceData?.data?.source_data_source_id;
    return typeof id === 'number' && id > 0 ? id : 0;
  };

  /**
   * Apply an existing server registration (from `/source-files/registered`) to
   * node session and open Data Profiling — used by both the pre-check path
   * and the onError safety-net path.
   */
  const applyExistingRegistration = (
    nodeId: string,
    match: { source_file_id: number; fields: Array<{ field_name: string; data_type: string; source_file_field_id?: number }> },
    options?: { silent?: boolean },
  ) => {
    const sourceFilesPayload = match.fields.map(field => ({
      source_file_id: match.source_file_id,
      source_file_field_id: field.source_file_field_id,
      field_name: field.field_name,
      field_data_type: field.data_type,
    }));

    updateSourceData(nodeId, {
      source_files: sourceFilesPayload,
      source_selected_table: selectedTable,
    });

    // Bring the canvas node in sync with how the registered-source flow marks
    // a source "configured" (matching useFinalizeRegisteredSourceMutation). If
    // Explorer reconciles for a node that wasn't yet completed (e.g. a
    // new-source flow hit a name collision), the gear icon would otherwise
    // still surface as incomplete.
    const snapshot = store.getState();
    const sourceNodeIndex = snapshot.canvas.present.nodes.findIndex(
      n => n.id === nodeId && n.type === 'source',
    );
    if (sourceNodeIndex >= 0) {
      const sourceNode = snapshot.canvas.present.nodes[sourceNodeIndex];
      const alreadyCompleted = Array.isArray(sourceNode.data?.completed_steps)
        && sourceNode.data.completed_steps.includes('configuration-details');
      if (!alreadyCompleted) {
        const nextNodes = [...snapshot.canvas.present.nodes];
        nextNodes[sourceNodeIndex] = {
          ...sourceNode,
          data: {
            ...sourceNode.data,
            mode: 'existing-source',
            completed_steps: ['configuration-details'],
          },
        } as typeof sourceNode;
        dispatch(
          syncCanvasState({
            nodes: nextNodes,
            edges: snapshot.canvas.present.edges,
          }),
        );
      }
    }

    // Tell the user why we skipped the POST and went straight to profiling.
    // Without this, a user creating what they believe is a fresh registration
    // for a name-collided table would land on Data Profiling with no idea
    // their column/dq selections from the form were not what got persisted.
    // Skip on the onError race path — the apiRequest interceptor already
    // toasted the "already exists" error, so an additional info toast would
    // just look contradictory.
    if (!options?.silent) {
      toast.info(
        `"${selectedTable}" is already registered against this source — using the existing registration.`,
      );
    }
    dispatch(
      openOverlay({
        type: 'data-profiling-confirmation',
        data: { nodeId },
      })
    );
  };

  const handleSave = async () => {
    if (!canSave) return;
    const nodeId = explorerDialogData?.nodeId;
    if (!nodeId) return;

    // Pre-check: revisits of an already-registered table would otherwise hit
    // 400 "Source file already exists" from POST /source-files. Silently
    // reconcile against the existing registration when we detect a match.
    // Falls through to the normal POST flow on miss OR if the pre-check itself
    // errors — keeping the new-source first-save path unchanged.
    const sourceDataSourceId = readSourceDataSourceId(nodeId);
    if (sourceDataSourceId > 0) {
      try {
        const registered = await sourceService.listRegisteredSourceFilesSilent(sourceDataSourceId);
        const match = registered.find(file => file.table_name === selectedTable);
        if (match) {
          applyExistingRegistration(nodeId, match);
          return;
        }
      } catch (preCheckError) {
        // Pre-check failed — fall through to POST so the existing flow is
        // never blocked by an auxiliary fetch. Log so a misconfigured backend
        // doesn't silently degrade to the duplicate-POST it's meant to prevent.
        console.warn(
          'Explorer pre-check (/source-files/registered) failed; falling through to POST.',
          preCheckError,
        );
      }
    }

    const payload = buildCreateSourceFileAndFieldsPayload({
      sourceEntry,
      sourceName,
      selectedTable,
      rows,
      sourceType: dataSourceType,
      fileExtras: isFileSystemSource ? fileExtras : undefined,
    });

    if (!payload) {
      toast.warning('Select at least one column to continue.');
      return;
    }

    createSourceFileAndFieldsMutation.mutate(payload, {
      onSuccess: response => {
        updateSourceData(nodeId, {
          source_files: response,
          source_selected_table: selectedTable,
        });

        dispatch(
          openOverlay({
            type: 'data-profiling-confirmation',
            data: { nodeId },
          })
        );
        toast.success('Source file and fields created successfully');
      },
      onError: async error => {
        const message = error instanceof Error ? error.message : String(error);
        // Safety net: a race could still produce the duplicate (e.g. server
        // registered the table between pre-check and POST). Reconcile the
        // same way the pre-check would have — but pass `silent: true` because
        // the apiRequest interceptor already toasted the "already exists"
        // error, and a second info toast would look contradictory.
        if (/source file already exists/i.test(message)) {
          try {
            const registered = await sourceService.listRegisteredSourceFilesSilent(
              sourceDataSourceId,
            );
            const match = registered.find(file => file.table_name === selectedTable);
            if (match) applyExistingRegistration(nodeId, match, { silent: true });
          } catch (reconcileError) {
            // apiRequest interceptor already toasted the network error; log
            // for diagnosis but don't double-toast.
            console.warn('Explorer reconcile after duplicate POST failed.', reconcileError);
          }
          return;
        }
        // apiRequest re-wraps every axios error as a plain `new Error(message)`
        // and toasts it from the interceptor before re-throwing. Don't try to
        // distinguish "axios vs. non-axios" here — that signal is gone by the
        // time we get the error. If a non-toasted error path ever surfaces
        // (would be a regression in the api client), the apiRequest interceptor
        // is the right place to fix it.
        console.warn('Source file POST failed:', message);
      },
    });
  };

  return (
    <Dialog open={isVisible} onOpenChange={handleOpenChange}>
      <DialogContent
        className={`max-w-4xl max-h-[80dvh] flex flex-col ${hasTableSelected ? 'h-[80dvh]' : 'h-auto'}`}
      >
        <DialogHeader className="!flex !flex-row items-center gap-2 justify-start shrink-0">
          <Button variant="ghost" size="icon" onClick={handleBack}>
            <ArrowLeftIcon className="size-4" />
          </Button>

          <DialogTitle className="text-xl font-semibold tracking-tight">Explorer</DialogTitle>
        </DialogHeader>

        <DialogBody className="flex-1 min-h-0 flex flex-col gap-4 overflow-hidden">
          <Form {...form}>
            <div className="space-y-4 shrink-0">
              <FormField
                control={form.control}
                name="connection"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-1.5">
                      {connectionLabel}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <InfoIcon className="size-3.5 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                          Connection used to fetch tables and columns.
                        </TooltipContent>
                      </Tooltip>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} readOnly />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="tableName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-1.5">
                      {tableLabel}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <InfoIcon className="size-3.5 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>{tableTooltip}</TooltipContent>
                      </Tooltip>
                    </FormLabel>
                    <FormControl>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger>
                          <SelectValue placeholder={tablePlaceholder} />
                        </SelectTrigger>
                        <SelectContent>
                          {selectableTables.map(table => (
                            <SelectItem key={table} value={table}>
                              {table}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                  </FormItem>
                )}
              />

              {showDelimiter && (
                <FormField
                  control={form.control}
                  name="fileDelimiter"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Delimiter</FormLabel>
                      <FormControl>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select delimiter" />
                          </SelectTrigger>
                          <SelectContent>
                            {DELIMITERS.map(d => (
                              <SelectItem key={d.name} value={d.name}>
                                {d.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                    </FormItem>
                  )}
                />
              )}

              {showJsonPath && (
                <FormField
                  control={form.control}
                  name="jsonPath"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>JSON Path</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. $.data.items[*]" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              )}

              {showDateFormat && (
                <FormField
                  control={form.control}
                  name="dateFormat"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Date Format</FormLabel>
                      <FormControl>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select date format" />
                          </SelectTrigger>
                          <SelectContent>
                            {DATE_FORMATS.map(format => (
                              <SelectItem key={format} value={format}>
                                {format}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                    </FormItem>
                  )}
                />
              )}
            </div>
          </Form>

          <div className="flex flex-col gap-2 flex-1 min-h-0">
            <p className="text-sm font-medium shrink-0">Which columns would you like to choose?</p>
            {!hasTableSelected && (
              <div className="rounded-md min-h-56 flex items-center justify-center border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
                {isFileSystemSource
                  ? 'Select a file to load columns.'
                  : 'Select a table to load columns.'}
              </div>
            )}

            {hasTableSelected && !fileExtrasReady && (
              <div className="rounded-md min-h-56 flex items-center justify-center border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
                {showDelimiter && !resolvedDelimiterValue
                  ? 'Select a delimiter to load columns.'
                  : 'Select a date format to load columns.'}
              </div>
            )}

            {hasTableSelected && fileExtrasReady && columnsQuery.isPending && (
              <div className="space-y-2 rounded-md border p-3">
                {[1, 2, 3, 4, 5].map(item => (
                  <Skeleton key={`explorer-loading-${item}`} className="h-10 w-full" />
                ))}
              </div>
            )}

            {hasTableSelected && fileExtrasReady && columnsQuery.isSuccess && (
              <div className="flex-1 min-h-0">
                <ExplorerColumnsTable
                  rows={rows}
                  dataTypeOptions={dataTypeOptions}
                  onToggleAll={checked => {
                    setRows(previousRows =>
                      previousRows.map(row => ({ ...row, selected: checked }))
                    );
                  }}
                  onToggleRow={(rowId, checked) => updateRow(rowId, { selected: checked })}
                  onTogglePrimaryKey={(rowId, checked) => updateRow(rowId, { primaryKey: checked })}
                  onDataTypeChange={(rowId, value) => updateRow(rowId, { dataType: value })}
                  onDqChecksChange={(rowId, value) => updateRow(rowId, { dqChecks: value })}
                />
              </div>
            )}

            {hasTableSelected && fileExtrasReady && columnsQuery.isError && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                {columnsQuery.error.message || 'Failed to load columns.'}
              </div>
            )}
          </div>
        </DialogBody>
        <DialogFooter className="flex items-center justify-end gap-2 shrink-0">
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!canSave} onClick={handleSave}>
            {createSourceFileAndFieldsMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExplorerDialog;
